import { useMutation } from "@apollo/client";
import { MultistepDialog, DialogStep } from "@blueprintjs/core";
import { Form, Formik } from "formik";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { ADD_SCHEDULEDJOB } from "graphql/mutations";
import { GET_SCHEDULES } from "graphql/queries";
import { NewScheduleState, ScheduleDoc } from "types";
import { SelectedProjectContext } from "utils/context";
import { createFromExisting, createScheduleJobObject, setInitalCreateScheduleState } from "utils/utils";

import Confirm from "./form/Confirm";

import ProjectVariables from "./form/ProjectVariables";
import Schedule from "./form/Schedule";
import { ScheduleToaster } from "utils/toasters";

type Props = {
    isOpen: boolean;
    toggle: () => void;
    clearEditAndClone: () => void;
    editing: boolean;
    cloning: boolean;
    schedule?: ScheduleDoc;
};

export default function ScheduleDialog({ isOpen, toggle, schedule, cloning, editing, clearEditAndClone }: Props) {
    const [project] = useContext(SelectedProjectContext);
    const [upsertSchedule] = useMutation<{ id: string }>(ADD_SCHEDULEDJOB);

    const [initialValues, setInitialValues] = useState<NewScheduleState>(
        setInitalCreateScheduleState(project ? project.userInputs : {})
    );

    useEffect(() => {
        let v;
        if (!editing && !cloning) {
            v = setInitalCreateScheduleState(project ? project.userInputs : {});
        }
        if (schedule) {
            if (editing) {
                v = createFromExisting(schedule, project?.userInputs, false);
            }
            if (cloning) {
                v = createFromExisting(schedule, project?.userInputs, true);
            }
        }
        if (v) {
            setInitialValues(v);
        }
    }, [cloning, editing, project, schedule]);

    useEffect(() => {
        if (editing && schedule) {
            const v = createFromExisting(schedule, project?.userInputs, false);
            setInitialValues(v);
        }
    }, [editing, project, schedule]);

    const handleSubmit = useCallback(
        async (values: NewScheduleState) => {
            const scheduleInfo = createScheduleJobObject(
                values,
                project!,
                schedule && editing ? schedule.id : undefined
            );
            await upsertSchedule({
                variables: { scheduleInfo },
                refetchQueries: [{ query: GET_SCHEDULES, variables: { projectId: project?.projectId } }]
            })
                .then((res) => {
                    ScheduleToaster.show({
                        message: `Successfully ${editing ? "Updated" : "Created"} Schedule.`,
                        intent: "success"
                    });
                })
                .catch((err) => {
                    ScheduleToaster.show({
                        message: `Failed to Create the Schedule.`,
                        intent: "danger"
                    });
                })
                .finally(() => {
                    setTimeout(() => {
                        toggle();
                        clearEditAndClone();
                    }, 0);
                });
        },

        [clearEditAndClone, editing, project, schedule, toggle, upsertSchedule]
    );

    return (
        <Formik
            onSubmit={async (values) => {
                await handleSubmit(values);
            }}
            enableReinitialize
            initialValues={initialValues}
        >
            {({ isSubmitting, values, submitForm, resetForm }) => {
                return (
                    <Form>
                        <MultistepDialog
                            title="New Schedule"
                            icon="new-object"
                            isOpen={isOpen}
                            onClose={() => {
                                resetForm();
                                clearEditAndClone();
                                toggle();
                            }}
                            finalButtonProps={{
                                onClick: submitForm,
                                disabled: isSubmitting,
                                loading: isSubmitting,
                                intent: editing ? "warning" : "primary",
                                text: editing ? "Update" : "Submit"
                            }}
                            enforceFocus={false}
                            usePortal={true}
                        >
                            <DialogStep
                                id="company-project-inputs"
                                title="Project Variables"
                                // nextButtonProps={{ disabled: true }}
                                panel={
                                    <>
                                        {/* <Collapse isOpen={true}>
                                            <Pre>{JSON.stringify(values, null, 2)}</Pre>
                                        </Collapse> */}
                                        <ProjectVariables userInputs={project?.userInputs} editing={editing} />
                                    </>
                                }
                            />

                            <DialogStep
                                id="schedule"
                                title="Schedule"
                                panel={
                                    <>
                                        {/* <Collapse isOpen={true}>
                                            <Pre>{JSON.stringify(values, null, 2)}</Pre>
                                        </Collapse> */}
                                        <Schedule />
                                    </>
                                }
                            />
                            <DialogStep
                                id="confirm"
                                title="Confirm"
                                panel={
                                    <>
                                        {/* <Collapse isOpen={true}>
                                            <Pre>{JSON.stringify(values, null, 2)}</Pre>
                                        </Collapse> */}
                                        <Confirm />
                                    </>
                                }
                            />
                        </MultistepDialog>
                    </Form>
                );
            }}
        </Formik>
    );
}
